import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getFirestore,
  collection,
  query,
  orderBy,
  doc,
  getDoc,
  getDocs,
  where,
  updateDoc,
} from "firebase/firestore";
import { getAuth, signOut } from "firebase/auth";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "stores/Store";
import { User, isProOrHigher, PRO_FEATURES_ENABLED } from "interfaces/user";
import { setUsername, setUser } from "stores/user/UserSlice";
import { setShowNavbar, setShowFriends } from "stores/uiState/UiStateSlice";
import Login from "components/Login";
import { getRankImage } from "utils/uiStateUtils";
// editIcon and checkmarkIcon no longer used — replaced by text button
import { defaultStartingElo } from "constants/gameDefaults";
import GameHistory from "components/GameHistory";
import { openCheckout, openCustomerPortal } from "utils/stripe";

export const MAX_USERNAME_LENGTH = 12;

function UserProfile() {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.userReducer.user);
  const { userUid } = useParams();
  const [userProfileData, setUserProfileData] = useState<User | null>(null);
  const [userRankPosition, setUserRankPosition] = useState<number>(1);
  const [isEditing, setIsEditing] = useState(false);
  const [username, setLocalUsername] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [winStats, setWinStats] = useState<{
    wins: number;
    total: number;
  } | null>(null);
  const spanRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    dispatch(setShowNavbar(true));
    dispatch(setShowFriends(true));
  }, [dispatch]);

  useEffect(() => {
    const fetchData = async () => {
      if (user) {
        const fetchedUserUid =
          userUid === undefined || userUid === "undefined" ? user.uid : userUid;
        const db = getFirestore();
        const userDoc = doc(db, "users", fetchedUserUid);
        const userSnapshot = await getDoc(userDoc);

        if (userSnapshot.exists()) {
          const userData = userSnapshot.data();

          const userProfile: User = {
            uid: fetchedUserUid,
            currentElo: userData?.currentElo || defaultStartingElo,
            username: userData?.username,
          };
          setLocalUsername(userData?.username || "");
          setUserProfileData(userProfile);

          const usersAboveCurrentUserQuery = query(
            collection(db, "users"),
            orderBy("currentElo", "desc"),
            where("currentElo", ">", userData.currentElo || defaultStartingElo),
          );
          const usersAboveSnapshot = await getDocs(usersAboveCurrentUserQuery);
          setUserRankPosition(usersAboveSnapshot.size + 1);
        }
      }
    };
    fetchData();
  }, [userUid, user]);

  const handleSignOut = async () => {
    try {
      const auth = getAuth();
      await signOut(auth);
      dispatch(setUser(null));
    } catch (error) {
      console.error("Error signing out: ", error);
    }
  };

  const navigate = useNavigate();

  const [currentText, setCurrentText] = useState(username);

  const handleInput = (event: React.FormEvent<HTMLSpanElement>) => {
    setCurrentText(event.currentTarget.textContent || "");
  };

  const updateUsername = async () => {
    setUsernameError("");
    const trimmed = currentText.trim();
    if (!trimmed) {
      setUsernameError("Username cannot be empty");
      return;
    }
    if (trimmed.length > MAX_USERNAME_LENGTH) {
      setUsernameError(
        `Username too long (max ${MAX_USERNAME_LENGTH} characters)`,
      );
      return;
    }
    if (trimmed === user?.username) {
      return;
    }
    try {
      const db = getFirestore();
      const usersCollection = collection(db, "users");
      const querySnapshot = await getDocs(
        query(usersCollection, where("username", "==", trimmed)),
      );
      if (!querySnapshot.empty) {
        setUsernameError("This username is already taken");
        setCurrentText(user?.username || username);
        setLocalUsername(user?.username || username);
        if (spanRef.current) {
          spanRef.current.textContent = user?.username || username;
        }
        return;
      }
      if (user) {
        const userDoc = doc(db, "users", user.uid);
        await updateDoc(userDoc, { username: trimmed });
        dispatch(setUsername(trimmed));
        setLocalUsername(trimmed);
      }
    } catch (error) {
      console.error("Error updating username: ", error);
    }
  };

  const handleEditClick = () => {
    if (isEditing) {
      updateUsername();
      setIsEditing(false);
    } else {
      setIsEditing(true);
      setUsernameError("");
      setTimeout(() => {
        const span = spanRef.current;
        if (span) {
          span.focus();
          const range = document.createRange();
          range.selectNodeContents(span);
          range.collapse(false); // Set to false to collapse the range to its end
          const selection = window.getSelection();
          if (selection) {
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
      }, 0);
    }
  };

  if (!user) {
    return <Login message="Please login to watch player profile" />;
  }

  if (!userProfileData) {
    return (
      <section className="UserProfile">
        <div className="heroContainer">
          <div className="headerContainer">
            <div className="title">No user found</div>
            <motion.button
              className="homeButton"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => navigate("/")}
            >
              Return to Home
            </motion.button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="UserProfile">
      <div className="heroContainer">
        <div className="headerContainer">
          <div className="headerRow">
            <div className="titleSection">
              <div className="titleRow">
                <span
                  ref={spanRef}
                  role="textbox"
                  contentEditable={isEditing}
                  onInput={handleInput}
                  onBlur={(e) => e.preventDefault()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      updateUsername();
                      setIsEditing(false);
                    }
                  }}
                  tabIndex={0}
                  className="username"
                >
                  {username}
                </span>
                {PRO_FEATURES_ENABLED &&
                  userProfileData.uid === user.uid &&
                  !isProOrHigher(user) && (
                    <motion.button
                      className="upgradeButton"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={openCheckout}
                    >
                      Upgrade to Pro
                    </motion.button>
                  )}
                {PRO_FEATURES_ENABLED &&
                  userProfileData.uid === user.uid &&
                  isProOrHigher(user) && (
                    <motion.button
                      className="upgradeButton proButton"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={openCustomerPortal}
                    >
                      PRO ✦
                    </motion.button>
                  )}
              </div>
              {userProfileData.uid === user.uid && (
                <motion.button
                  className="changeNameButton"
                  whileHover={{ opacity: 1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleEditClick()}
                >
                  {isEditing ? "Confirm" : "Change name"}
                </motion.button>
              )}
              {usernameError && (
                <p className="usernameError">{usernameError}</p>
              )}
            </div>
            <div className="rankSection">
              <h2 className="headline">
                <img src={getRankImage(userRankPosition)} alt="Rank" />
                <p>{userProfileData.currentElo} LP</p>
                {winStats && winStats.total > 0 && (
                  <span className="winrate">
                    {winStats.wins}W {winStats.total - winStats.wins}L (
                    {Math.round((winStats.wins / winStats.total) * 100)}%)
                  </span>
                )}
              </h2>
              {userProfileData.uid === user.uid && (
                <motion.button
                  className="logOutButton"
                  whileHover={{ opacity: 1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleSignOut()}
                >
                  Log out
                </motion.button>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="gameHistoryContainer">
        <GameHistory userId={userProfileData.uid} onStatsLoaded={setWinStats} />
      </div>
    </section>
  );
}

export default UserProfile;
